import { useCallback, useEffect, useMemo, useState, memo } from "react";
import type { CSSProperties } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Scissors, Clock, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence, LazyMotion, domAnimation } from "framer-motion";

type AgendamentoHora = {
  hora: string;
  profissional: string;
};

type MovementData = {
  date: string;
  total: number;
  byHour: Record<string, number>; // "09" -> 2
  byHourProfissional: Record<string, AgendamentoHora[]>; // "15" -> [{hora: "15:00", profissional: "Jacson"}, ...]
  horaAtual: string; // "15:30"
};

function getBrazilTimeHHMM() {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());
}

function getBrazilCurrentTime() {
  const now = new Date();
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);
}

/* ===================== COMPONENTS ===================== */

function StatusRing({
  level,
  colorClass,
  className,
}: {
  level: 0 | 1 | 2 | 3;
  colorClass: string;
  className?: string;
}) {
  const segments = [
    "M10 10 L10 2 A8 8 0 0 1 18 10 Z",
    "M10 10 L18 10 A8 8 0 0 1 10 18 Z",
    "M10 10 L10 18 A8 8 0 0 1 2 10 Z",
  ] as const;

  return (
    <svg viewBox="0 0 20 20" className={cn("h-3.5 w-3.5", className)} aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="8" className="fill-none stroke-border" strokeWidth="2" />
      {segments.slice(0, level).map((d) => (
        <path key={d} d={d} className={colorClass} />
      ))}
    </svg>
  );
}

// Custom Label with filled background
const Label = memo(({ name, subtext, colorClass, className }: { name?: string, subtext?: string, colorClass: string, className?: string }) => {
  if (!name && !subtext) return null;
  
  // Use solid fills as requested
  const bgClass = colorClass.includes('sky') 
    ? 'bg-sky-500' 
    : colorClass.includes('warning') 
      ? 'bg-amber-500' 
      : 'bg-zinc-800';
  
  return (
    <div className={cn("absolute -top-10 left-1/2 -translate-x-1/2 z-30 pointer-events-none flex flex-col items-center animate-in fade-in zoom-in duration-300", className)}>
      <div className={cn("px-2.5 py-1 rounded-md text-white font-black text-[10px] uppercase tracking-wider shadow-2xl border border-white/20 whitespace-nowrap flex flex-col items-center", bgClass)}>
        <span className="drop-shadow-md">{name}</span>
        {subtext && (
          <span className="text-[9px] font-black flex items-center gap-1 border-t border-white/20 mt-0.5 pt-0.5 w-full justify-center">
            <Clock className="w-2.5 h-2.5" /> {subtext}
          </span>
        )}
        <div className={cn("absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45", bgClass)} />
      </div>
    </div>
  );
});
Label.displayName = "Label";

const BarberChair = memo(({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 80" className={cn("w-16 h-20 drop-shadow-2xl", className)} fill="none" style={{ overflow: "visible" }}>
    <defs>
      <linearGradient id="chairGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ef4444" />
        <stop offset="100%" stopColor="#991b1b" />
      </linearGradient>
      <linearGradient id="metalGradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#94a3b8" />
        <stop offset="100%" stopColor="#475569" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="76" rx="20" ry="5" fill="url(#metalGradient)" />
    <rect x="28" y="56" width="8" height="20" rx="1" fill="url(#metalGradient)" />
    <rect x="10" y="40" width="44" height="14" rx="4" fill="url(#chairGradient)" stroke="#450a0a" />
    <rect x="14" y="8" width="36" height="38" rx="6" fill="url(#chairGradient)" stroke="#450a0a" />
    <rect x="4" y="38" width="8" height="3" rx="1.5" fill="#64748b" />
    <rect x="52" y="38" width="8" height="3" rx="1.5" fill="#64748b" />
  </svg>
));

const ClienteNaCadeira = memo(({ toneClass, flip }: { toneClass: string; flip?: boolean }) => {
  return (
    <div className="relative">
      <div className={cn("transition-transform", flip && "scale-x-[-1]")}>
        <motion.svg
          viewBox="0 0 40 70"
          className="w-12 h-20"
          fill="none"
          style={{ overflow: "visible" }}
          animate={{ y: [0, -1, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="20" cy="22" r="7" className={toneClass} />
          <path d="M12 30 Q20 28 28 30 L32 50 Q20 54 8 50 Z" fill="#0f172a" />
          <path d="M8 32 Q20 28 32 32 L34 58 Q20 62 6 58 Z" fill="#1e293b" opacity="0.9" />
        </motion.svg>
      </div>
      {/* Removed client label as requested */}
    </div>
  );
});

const Barber = memo(({ color, flip, nome, isServicing }: { color: string; flip?: boolean; nome?: string; isServicing?: boolean }) => {
  return (
    <div className="relative">
      <div className={cn("transition-transform", flip && "scale-x-[-1]")}>
        <motion.svg
          viewBox="0 0 50 70"
          className="w-16 h-20"
          fill="none"
          style={{ overflow: "visible" }}
          animate={{ y: [0, -1, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* Head */}
          <circle cx="25" cy="18" r="8" className={color} />
          
          {/* Body */}
          <path d="M17 28 L17 55 Q25 60 33 55 L33 28 Q25 25 17 28" fill="#1e293b" />
          <path d="M19 32 L19 55 L31 55 L31 32 Q25 30 19 32" fill="#334155" />
          
          {/* Arm Left - WITH SCISSORS */}
          <motion.g 
            animate={isServicing ? { rotate: [-70, -100, -70] } : { rotate: [-15, -20, -15] }} 
            transition={{ duration: isServicing ? 0.8 : 3, repeat: Infinity, ease: "easeInOut" }} 
            style={{ transformOrigin: "20px 32px" }}
          >
            <rect x="15" y="30" width="4" height="15" rx="2" className={color} />
            {/* Scissor Base */}
            <g transform="translate(14, 42)">
              {/* Blade 1 */}
              <motion.path 
                d="M2 0 L6 10" 
                stroke="#94a3b8" 
                strokeWidth="1.5"
                animate={{ rotate: [0, 20, 0] }}
                transition={{ duration: 0.4, repeat: Infinity }}
                style={{ originX: "2px", originY: "0px" }}
              />
              {/* Blade 2 */}
              <motion.path 
                d="M2 0 L-2 10" 
                stroke="#94a3b8" 
                strokeWidth="1.5"
                animate={{ rotate: [0, -20, 0] }}
                transition={{ duration: 0.4, repeat: Infinity }}
                style={{ originX: "2px", originY: "0px" }}
              />
              <circle cx="2" cy="0" r="1.5" fill="#475569" />
            </g>
          </motion.g>

          {/* Arm Right - WITH COMB */}
          <motion.g 
            animate={isServicing ? { rotate: [70, 100, 70] } : { rotate: [15, 20, 15] }} 
            transition={{ duration: isServicing ? 0.7 : 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }} 
            style={{ transformOrigin: "30px 32px" }}
          >
            <rect x="31" y="30" width="4" height="15" rx="2" className={color} />
            {/* Comb */}
            <g transform="translate(32, 43) rotate(20)">
              <rect x="0" y="0" width="10" height="3" rx="0.5" fill="#0f172a" />
              {[0, 2, 4, 6, 8].map(x => (
                <rect key={x} x={x+0.5} y="3" width="0.5" height="4" fill="#0f172a" />
              ))}
            </g>
          </motion.g>
        </motion.svg>
      </div>
      <Label name={nome} colorClass={color} />
    </div>
  );
});

const ClienteSentado = memo(({ toneClass, horario }: { toneClass: string; horario?: string }) => {
  return (
    <div className="relative">
      <Label subtext={horario} colorClass="bg-zinc-800" className="-top-7" />
      <motion.svg
        animate={{ y: [0, -1, 0] }}
        transition={{ duration: 2.5, repeat: Infinity }}
        viewBox="0 0 32 56"
        className="w-10 h-14"
        fill="none"
        style={{ overflow: "visible" }}
      >
        <circle cx="16" cy="20" r="6" className={toneClass} />
        <path d="M10 28 L10 40 L22 40 L22 28 Q16 26 10 28" className={toneClass} />
        <rect x="10" y="40" width="4" height="8" rx="2" className={toneClass} />
        <rect x="18" y="40" width="4" height="8" rx="2" className={toneClass} />
      </motion.svg>
    </div>
  );
});

const BancoEspera = memo(() => (
  <svg viewBox="0 0 200 40" className="w-full h-10" fill="none">
    <rect x="5" y="10" width="190" height="20" rx="4" fill="#0f172a" stroke="#334155" />
    <rect x="10" y="30" width="4" height="10" rx="1" fill="#334155" />
    <rect x="186" y="30" width="4" height="10" rx="1" fill="#334155" />
  </svg>
));

const Espelho = memo(() => (
  <svg viewBox="0 0 40 60" className="w-12 h-16" fill="none">
    <rect x="2" y="2" width="36" height="56" rx="4" fill="#334155" stroke="#475569" />
    <rect x="4" y="4" width="32" height="52" rx="3" fill="#94a3b8" fillOpacity="0.1" />
  </svg>
));

const BarberPole = memo(() => (
  <div className="relative w-5 h-14 bg-white rounded overflow-hidden border border-slate-700 shadow-xl">
    <motion.div 
      animate={{ y: [0, -28] }}
      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      className="absolute top-0 left-0 w-full"
    >
      {[...Array(6)].map((_, i) => (
        <div key={i}>
          <div className="h-5 w-full bg-red-600 rotate-[20deg] scale-150" />
          <div className="h-5 w-full bg-blue-700 rotate-[20deg] scale-150" />
        </div>
      ))}
    </motion.div>
  </div>
));

/* ===================== MAIN COMPONENT ===================== */

export default function MovimentacaoDia() {
  const [movement, setMovement] = useState<MovementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [horaAtual, setHoraAtual] = useState(getBrazilTimeHHMM());
  const [horaAtualFull, setHoraAtualFull] = useState(getBrazilCurrentTime());
  const [horariosPicoSemana, setHorariosPicoSemana] = useState<string[]>([]);

  const fetchMovement = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-day-movement", { body: {} });
      if (error) throw error;
      setMovement(data as MovementData);
    } catch (e) {
      console.error(e);
      setErrorMsg("Erro ao carregar dados.");
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  const fetchHorariosPicoSemana = useCallback(async () => {
    try {
      const hoje = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase.from('agendamentos_robustos').select('HORA').eq('DATA', hoje).in('STATUS', ['AGENDADO', 'REAGENDADO', 'CONCLUÍDO']);
      if (error) throw error;
      const contagem: Record<string, number> = {};
      data?.forEach(item => { if (item.HORA) { const h = item.HORA.toString().substring(0, 2); contagem[h] = (contagem[h] || 0) + 1; } });
      const ordenados = Object.entries(contagem).sort(([, a], [, b]) => b - a).slice(0, 3).map(([h]) => h);
      setHorariosPicoSemana(ordenados);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchMovement(true);
    fetchHorariosPicoSemana();
    const id = setInterval(() => fetchMovement(false), 30000);
    return () => clearInterval(id);
  }, [fetchMovement, fetchHorariosPicoSemana]);

  const { agendamentosFuturos, atendimentosAtuais, agendamentosNoBanco, joaoEmAtendimento, jacsonEmAtendimento } = useMemo(() => {
    const byHourProfissional = movement?.byHourProfissional ?? {};
    const [hAt, mAt] = horaAtualFull.split(":").map(Number);
    const atualMin = hAt * 60 + mAt;

    const todos = Object.values(byHourProfissional).flat().sort((a, b) => a.hora.localeCompare(b.hora));
    
    // Contagem de agendamentos ainda não realizados
    const futurosCount = Object.entries(movement?.byHour ?? {}).reduce((acc, [h, count]) => {
      const hMin = parseInt(h) * 60;
      return acc + (hMin >= (hAt * 60) ? count : 0);
    }, 0);

    const joaoJobs = todos.filter(a => a.profissional?.toLowerCase().includes("joao") || a.profissional?.toLowerCase().includes("joão"));
    const jacsonJobs = todos.filter(a => a.profissional?.toLowerCase().includes("jacson"));

    const cadeiras = [];
    const banco = [];

    // João (Esquerda / Azul)
    if (joaoJobs.length > 0) {
      cadeiras.push({ position: "left", horario: joaoJobs[0].hora, toneClass: "fill-sky-500", profissional: "João" });
      joaoJobs.slice(1).forEach(j => banco.push({ ...j, side: "right" as const }));
    }

    // Jacson (Direita / Laranja)
    if (jacsonJobs.length > 0) {
      cadeiras.push({ position: "right", horario: jacsonJobs[0].hora, toneClass: "fill-warning", profissional: "Jacson" });
      jacsonJobs.slice(1).forEach(j => banco.push({ ...j, side: "left" as const }));
    }

    return { 
      agendamentosFuturos: futurosCount,
      atendimentosAtuais: cadeiras, 
      agendamentosNoBanco: banco,
      joaoEmAtendimento: joaoJobs.length > 0,
      jacsonEmAtendimento: jacsonJobs.length > 0
    };
  }, [movement, horaAtualFull]);

  const statusInfo = useMemo(() => {
    if (agendamentosFuturos === 0) return { texto: "Tranquilo", level: 1, class: "text-success", fill: "fill-success" };
    if (agendamentosFuturos <= 4) return { texto: "Pouco movimento", level: 1, class: "text-success", fill: "fill-success" };
    if (agendamentosFuturos <= 8) return { texto: "Moderado", level: 2, class: "text-warning", fill: "fill-warning" };
    return { texto: "Movimentado", level: 3, class: "text-destructive", fill: "fill-destructive" };
  }, [agendamentosFuturos]);

  const maxCount = Math.max(...Object.values(movement?.byHour ?? {}), 1);
  const horasOperacao = Array.from({ length: 12 }, (_, i) => String(i + 9).padStart(2, "0"));

  return (
    <Card className="bg-card/95 backdrop-blur-sm border-primary/20 overflow-hidden shadow-2xl">
      <CardHeader className="pb-2 border-b border-border/10">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2 font-black">
              <Scissors className="h-6 w-6 text-primary" />
              MOVIMENTAÇÃO DO DIA
            </CardTitle>
            <p className="text-sm text-muted-foreground font-medium">Situação dos agendamentos em tempo real</p>
          </div>
          <div className="flex items-center gap-2 text-xs font-bold bg-muted/30 rounded-full px-3 py-1.5 border border-border/20">
            <StatusRing level={statusInfo.level as any} colorClass={statusInfo.fill} />
            <span className={statusInfo.class}>{statusInfo.texto.toUpperCase()}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="grid lg:grid-cols-2">
          {/* Ilustração Barbearia */}
          <div className="relative bg-zinc-950 min-h-[320px] border-r border-border/10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/50 to-black/80" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-zinc-900" />
            
            {/* Elementos Centrais */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pb-12 pointer-events-none">
              <motion.span animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity }} className="text-[120px] font-black leading-none text-primary opacity-20 blur-sm">{agendamentosFuturos}</motion.span>
              <span className="text-[100px] font-black leading-none text-primary absolute drop-shadow-[0_0_30px_rgba(234,179,8,0.3)]">{agendamentosFuturos}</span>
            </div>

            {/* Decoração */}
            <div className="absolute top-6 left-6"><BarberPole /></div>
            <div className="absolute top-6 right-6"><BarberPole /></div>
            <div className="absolute top-4 left-[25%]"><Espelho /></div>
            <div className="absolute top-4 right-[25%]"><Espelho /></div>

            {/* Cadeiras e Atendimento */}
            <div className="absolute top-24 left-[20%] -translate-x-1/2">
              <BarberChair />
              {atendimentosAtuais.find(a => a.position === "left") && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-10 scale-110">
                  <ClienteNaCadeira toneClass={atendimentosAtuais.find(a => a.position === "left")!.toneClass} />
                </div>
              )}
            </div>

            <div className="absolute top-24 right-[20%] translate-x-1/2">
              <BarberChair className="scale-x-[-1]" />
              {atendimentosAtuais.find(a => a.position === "right") && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-10 scale-110">
                  <ClienteNaCadeira toneClass={atendimentosAtuais.find(a => a.position === "right")!.toneClass} flip />
                </div>
              )}
            </div>

            {/* Barbeiros - Posicionados nas extremidades para evitar proximidade excessiva */}
            <div className={cn("absolute transition-all duration-700", joaoEmAtendimento ? "top-20 left-[12%]" : "top-16 left-[5%]")}>
              <Barber color="fill-sky-500" nome="JOÃO" isServicing={joaoEmAtendimento} />
            </div>
            <div className={cn("absolute transition-all duration-700", jacsonEmAtendimento ? "top-20 right-[12%]" : "top-16 right-[5%]")}>
              <Barber color="fill-warning" flip nome="JACSON" isServicing={jacsonEmAtendimento} />
            </div>

            {/* Banco de Espera */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[90%]">
              <BancoEspera />
              <div className="absolute -top-10 left-0 right-0 flex justify-between px-12">
                {/* Lado Esquerdo do banco (João) */}
                <div className="flex gap-4">
                  {agendamentosNoBanco.filter(a => a.side === "left").slice(0, 2).map((ag, i) => (
                    <ClienteSentado key={i} toneClass="fill-sky-500" horario={ag.hora} />
                  ))}
                </div>
                {/* Lado Direito do banco (Jacson) */}
                <div className="flex gap-4 flex-row-reverse">
                  {agendamentosNoBanco.filter(a => a.side === "right").slice(0, 2).map((ag, i) => (
                    <ClienteSentado key={i} toneClass="fill-warning" horario={ag.hora} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico Lateral */}
          <div className="p-6 flex flex-col gap-6 bg-zinc-900/10">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                <Clock className="h-4 w-4" /> FLUXO DE HORÁRIOS
              </h3>
              <span className="text-[10px] font-bold bg-primary/10 text-primary px-2 py-0.5 rounded border border-primary/20">{horaAtual}</span>
            </div>

            <div className="flex items-end gap-1.5 h-48 p-4 bg-black/20 rounded-xl border border-white/5">
              {horasOperacao.map((h) => {
                const count = movement?.byHour?.[h] ?? 0;
                const hPico = horariosPicoSemana.includes(h) && count > 0;
                const height = count > 0 ? Math.max(15, (count / maxCount) * 100) : 5;
                return (
                  <div key={h} className="flex-1 flex flex-col items-center h-full justify-end group">
                    <span className={cn("text-[10px] font-black mb-1 transition-all", count > 0 ? "text-white scale-110" : "text-zinc-600 opacity-0 group-hover:opacity-100")}>{count || ""}</span>
                    <motion.div 
                      key={count} 
                      initial={{ height: 0 }} 
                      animate={{ height: `${height}%` }} 
                      className={cn("w-full rounded-t-md transition-all relative shadow-[0_0_15px_rgba(0,0,0,0.5)]", hPico ? "bg-destructive shadow-[0_5px_15px_rgba(239,68,68,0.4)]" : count > 0 ? "bg-primary shadow-[0_5px_15px_rgba(234,179,8,0.2)]" : "bg-zinc-800")}
                    >
                      {hPico && <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full animate-ping" />}
                    </motion.div>
                    <span className={cn("text-[9px] font-bold mt-2", h === horaAtual.split(":")[0] ? "text-primary scale-125" : "text-zinc-500")}>{h}</span>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-auto">
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-white/5 flex flex-col gap-1">
                <span className="text-[10px] font-bold text-zinc-500 uppercase">Pico do Dia</span>
                <span className="text-sm font-black text-destructive">{horariosPicoSemana[0] ? `${horariosPicoSemana[0]}:00` : '--:--'}</span>
              </div>
              <div className="p-3 bg-zinc-800/50 rounded-lg border border-white/5 flex flex-col gap-1">
                <span className="text-[10px] font-bold text-zinc-500 uppercase">Total Agendados</span>
                <span className="text-sm font-black text-primary">{movement?.total ?? 0} CLIENTES</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
